/**
 * "For You": one ordered stream of cards per person, like a social feed, but FINITE and ordered by the moment map
 * (ROLE_ONTOLOGY.md), never by engagement. It ends with "You're all caught up" (FRAMEWORK: the app is an instrument).
 * Pure function over the same data the apps already load, so web and phone show identical feeds (T7).
 */
import { refresherFor } from "./checkin.ts";
import { dayWindow, type HoursSource } from "./hours.ts";
import { isLate, type ExpectedCheck, type Finding } from "./schedule.ts";
import type { HowTo } from "./howto.ts";
import type { QuickAction } from "./actions.ts";
import type { IntegrityFlag } from "./integrity.ts";
import type { ZeroReportNote } from "./zeroreports.ts";
import type { OrderNeeds } from "./ordering.ts";
import type { ShiftNoticeView } from "./handoff.ts";
import type { FoodAtRisk } from "./foodstore.ts";

export type FeedRole = "staff" | "manager" | "owner";
export type Moment = "before" | "opening" | "day" | "close" | "evening";

export type FeedCard =
  | { kind: "gate"; id: string; title: string; body: string; action: { label: string; to: "checkin" | "learn" } }
  | { kind: "open_for_food"; id: string; ready: boolean; missing: { key: string; label: string }[]; /** Every missing check is a first-day one: a start, not a red "Not yet". */ firstDay?: boolean }
  | { kind: "check"; id: string; check: ExpectedCheck & { satisfied?: boolean }; late: boolean; howTo?: HowTo }
  | { kind: "reminder"; id: string; from: string; body: string }
  | { kind: "finding"; id: string; finding: Finding; actions: QuickAction[] }
  | { kind: "order"; id: string; needs: OrderNeeds }
  | { kind: "worth_a_look"; id: string; flag: IntegrityFlag }
  /** Owner only (zeroreports.ts): many shifts with no sick report. Counts, no names. */
  | { kind: "zero_reports"; id: string; note: ZeroReportNote }
  | { kind: "caught_early"; id: string; count: number }
  | { kind: "summary"; id: string; title: string; body: string }
  | { kind: "refresher"; id: string; title: string; text: string; lessonId: string }
  /**
   * `wantMore`: a manager whose day has no checklist items yet is offered the closing routine here ("Want more?", a new place
   * starts lean), from the day after setup on: never on the setup day itself (owner, 2026-10-12), never for staff.
   */
  | { kind: "caught_up"; id: string; title: string; body: string; wantMore?: true }
  /** A private heads-up after a failure (review.ts): one skill tip, one habit tip. Only ever in the recipient's own feed. */
  | { kind: "tip"; id: string; tip: FeedTip }
  /** Thanks for a report (review.ts): the reporter is never the target. */
  | { kind: "thanks"; id: string; body: string }
  /** First-day mode (docs/ux/LAUNCH-PROPOSALS.md Wave 1 #2): the owner's one first action, done together step by step. */
  | { kind: "first_check"; id: string; title: string; body: string; check: ExpectedCheck & { satisfied?: boolean }; howTo?: HowTo; /** Not checked in yet: step 1 is the check-in (FIRST_DAY_CHECKIN_ACTION), then the calibration (audit #8). */ checkinFirst?: true }
  /** First-day mode, after that first record: it's already in the inspection binder. */
  | { kind: "first_record"; id: string; title: string; body: string }
  /** Handed to me: a report or the shift, routed to the person in charge (handoff.ts). No push yet: this card is it. */
  | { kind: "handoff"; id: string; notice: ShiftNoticeView }
  /** "Ask them to fix" from a manager's log review (teamwork.ts): private, at the top, with a one-tap fix of that record. */
  | { kind: "fix_ask"; id: string; headline: string; entryId: string }
  /** Food past its date or its time (foodstore.ts foodAtRisk): for whoever is on shift, one tap to the discard log (F1). */
  | { kind: "throw_out"; id: string; food: FoodAtRisk };

export interface FeedTip { id: string; at: string; headline: string; skillTip?: { text: string; source: string }; habitTip?: string; lessonId?: string; ackedAt?: string }

export interface FeedInput {
  role: FeedRole;
  moment: Moment;
  now: string;
  employeeId?: string;
  date: string;
  gate?: { ready: boolean; blocking: { kind: string; title: string }[] };
  hasCheckin?: boolean;
  openForFood?: { ready: boolean; missing: { key: string; label: string }[] };
  expected: (ExpectedCheck & { satisfied?: boolean })[];
  findings: Finding[];
  reminders?: { at: string; message: string }[];
  /** What is on the supply list right now (core ordering.ts orderNeeds), managers only. */
  orderNeeds?: OrderNeeds;
  integrity?: IntegrityFlag[];
  /** Owners only; already hidden by "Not now" when absent (zeroreports.ts). */
  zeroReports?: ZeroReportNote;
  caughtEarlyToday?: number;
  controlled?: { every: boolean; missing?: string };
  /** My private tips (GET /me/tips). The newest one not yet acknowledged is shown. */
  tips?: FeedTip[];
  /** "Ask them to fix" notes for me, not yet answered (GET /me/tips with `fix`). Shown at the top, newest first. */
  fixAsks?: { id: string; headline: string; entryId: string }[];
  /** Owner in the evening: the close-of-day line (teamwork.ts closeOfDay), added to the one line. */
  teamDay?: string;
  /** Thank-yous for my reports today (today.me.notices). */
  notices?: { id: string; at: string; message: string }[];
  /** Local date this location was set up (from Location.createdAt). That day, the owner gets first-day mode. */
  startedOn?: string;
  /** Records logged here today, of any kind. First-day mode's first card shows only before the first one. */
  recordsToday?: number;
  /**
   * I'm a person in charge on role staff (a shift lead, today.me.isPersonInCharge). I get the open problems a manager
   * gets (reports, readings needing a fix, late checks), not "all caught up" while a 48°F fridge report is open (audit #10).
   */
  inCharge?: boolean;
  /** Hand-offs to me today (today.me.handoffs), newest first. */
  handoffs?: ShiftNoticeView[];
  /** The weekly owner summary (analytics.ts weeklySummary): a card for owners on Mondays. */
  weekly?: Pick<import("./analytics.ts").WeeklySummary, "text" | "showToday">;
  /** Food to throw out now (foodstore.ts foodAtRisk; today.foodAtRisk). Everyone on shift gets these cards (F1). */
  foodToThrow?: FoodAtRisk[];
}

/**
 * FOOD AT RISK FIRST (persona review 2026-10-01 A1/A9). At 7:30 am a 45°F pastry case sat 2.8 screens down, under the
 * owner's own check-in and three routine checks, because the feed was built in a fixed order. The rule now:
 *
 * | Rule | Why |
 * |---|---|
 * | F1 Food at risk is the first card: a TCS reading out of its limit (the rules below) and any food past its date or its time | The food is unsafe NOW. Food Code 3-501.16(A)(2): TCS food "shall be maintained ... At 5°C (41°F) or less"; 3-501.18(A): date-marked food "shall be discarded" once past 3-501.17(A). A routine check can wait ten minutes; the food can't |
 * | F2 It ranks above routine checks, reminders, "Open for food" and a manager's or owner's own check-in prompt | The owner may hand it to whoever is on shift (nag) or write what was done, neither of which is food work |
 * | F3 Staff behind their own check-in gate still see the gate first, then the food at risk | The gate is the one hard block (T1, Food Code 2-201.12: the person in charge excludes an ill worker). Handling the food is food work, so the worker checks in first |
 * | F4 Everyone on shift sees it, not only managers: the reading's own fix and nag (no Remind or Call, which are a manager's) | A barista alone next to a 45°F case is the person who can move the food (2-103.11: the person in charge ensures it, whoever is there does it) |
 * | F5 The Next card and the deck read the same rule (next.ts rankNext bucket "food_risk") | One order on every screen (T7) |
 */
export const FOOD_AT_RISK_RULES: ReadonlySet<string> = new Set([
  // 3-501.16(A)(2) cold holding at 41°F or less; 3-501.16(A)(1) hot holding at 135°F (130°F for chart roasts).
  "fridge", "cold_hold", "hot_hold", "hot_hold_roast",
  // 3-501.14(A) cooling: 70°F in 2 hours, 41°F in 6.
  "cool_2h", "cool_6h",
  // 3-401.11 cooking and 3-403.11 reheating temperatures not reached.
  "cook_poultry", "cook_ground", "cook_whole", "cook_roast", "reheat",
  // 3-202.11 cold food received above 41°F.
  "recv_cold",
]);

/** F1: a failed reading whose broken rule puts TCS food at risk now (not a missing soap or a weak sanitizer). */
export function isFoodAtRiskFinding(f: Pick<Finding, "kind" | "ruleIds">): boolean {
  return f.kind === "failed_check" && (f.ruleIds ?? []).some((r) => FOOD_AT_RISK_RULES.has(r));
}

export const WEEKLY_SUMMARY_ID = "weekly-summary";

/** The owner's first check is the fridge log, the one temperature ask Essentials makes twice a day (owner, 2026-10-11: one simple thing). */
export const FIRST_CHECK_TITLE = "Let's do your first check together: log your fridges (2 minutes)";
export const FIRST_RECORD_TITLE = "Your first record.";
/**
 * Audit #8 (2026-09-29): the first-day card sent the owner to calibrate before checking in, so day one read "Took
 * readings without a check-in" (not controlled). Until the owner checks in, the card's step 1 is the check-in.
 */
export const FIRST_DAY_CHECKIN_TITLE = "Your first day: check in, then log your fridges";
export const FIRST_DAY_CHECKIN_BODY = "Step 1: check in. A few health questions, then wash your hands (30 seconds). Step 2: log each fridge and freezer temperature (2 minutes).";
export const FIRST_DAY_CHECKIN_ACTION = "Start step 1: check in";
export const FIRST_RECORD_BODY = "If an inspector walked in now, this is in your binder.";

/**
 * First-day mode for the owner, on the day the location was set up: before any record, one card (log the fridges,
 * step by step); once that first fridge log is saved, one card saying it's in the binder. Null on any other day.
 */
export function firstDayMode(i: Pick<FeedInput, "role" | "startedOn" | "date" | "expected" | "recordsToday">): { stage: "first_check"; check: FeedInput["expected"][number] } | { stage: "first_record" } | null {
  if (i.role !== "owner" || !i.startedOn || i.startedOn !== i.date) return null;
  const cal = i.expected.find((e) => e.templateId === "cold_units");
  if (!cal) return null;
  if (cal.satisfied) return { stage: "first_record" };
  return (i.recordsToday ?? 0) === 0 ? { stage: "first_check", check: cal } : null;
}

/**
 * The confirmation right after the owner's FIRST save (setup gap 2, task 12 item 3): the record they just made is in the
 * inspection binder, with one link to it. It comes first; the card prompt (payments.ts) waits for the person to leave.
 * "First" = the owner, on the day the location was set up, with no record before this one. Stateless: the caller counts
 * today's records before the save. (The feed's "Your first record." card, firstDayMode, is the calibration-only twin.)
 */
export const FIRST_SAVE_TITLE = "Your first record is in the binder.";
export const FIRST_SAVE_LINK = "See your binder";
export function isFirstSave(i: { role: string; startedOn?: string; date: string; recordsBefore?: number }): boolean {
  return i.role === "owner" && !!i.startedOn && i.startedOn === i.date && i.recordsBefore === 0;
}

/** Which part of the day it is, from local minutes since midnight and the location's hours. */
export function momentOf(nowMin: number, openMin: number, closeMin: number): Moment {
  if (nowMin < openMin - 60) return "before";
  if (nowMin < openMin + 60) return "opening";
  if (nowMin < closeMin - 60) return "day";
  if (nowMin < closeMin + 60) return "close";
  return "evening";
}

/** The moment of the day from weekly hours (hours.ts dayWindow, H5). A closed day is "evening": nothing to open or close. */
export function momentAt(loc: HoursSource, now: Date | string, tz: string): Moment {
  const w = dayWindow(loc, now, tz);
  return w.closed ? "evening" : momentOf(w.nowMin, w.openMin, w.closeMin);
}

/**
 * The greeting, from the SAME moment the moment pill shows (FRAMEWORK MP2: two lines on one screen never say opposite
 * things). "Good morning" beside a "Closing" pill was the bug: the greeting used a clock, the pill the location's hours.
 * The moment decides; the hour (in the location's zone) only picks between morning and afternoon within it.
 */
export function greetingFor(moment: Moment, hour: number): string {
  if (moment === "close") return "Closing time";
  if (moment === "evening") return "Good evening";
  if (moment === "before" || moment === "opening") return hour < 12 ? "Good morning" : "Hello";
  return hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Hello";
}

/** Cards that are something still to do: while one shows, the feed never ends with "You're all caught up" (audit #14b). */
const OPEN_KINDS = new Set<FeedCard["kind"]>(["gate", "check", "finding", "first_check", "fix_ask", "handoff", "reminder", "order", "throw_out"]);
export const END_OPEN_TITLE = "That's everything for now";
export const END_OPEN_BODY = "Work the cards above, top first. We'll let you know when something else is due.";

/**
 * Offer "Want more?" (the closing routine, one tap) under "You're all caught up"? A manager, a day with no checklist item on it,
 * and a day after the one the place was set up on: the setup day is for the first checks, not for adding more (owner, 2026-10-12).
 * No `startedOn` (an older place, or a caller without it): offered.
 */
export function wantMoreOffer(i: Pick<FeedInput, "role" | "expected" | "startedOn" | "date">): boolean {
  return i.role !== "staff" && !i.expected.some((e) => e.templateId === "task_done") && (!i.startedOn || i.date > i.startedOn);
}

export function forYouFeed(i: FeedInput): FeedCard[] {
  const cards: FeedCard[] = [];
  const manager = i.role !== "staff";
  // First day: one card on top. Before the first record it replaces the red opening list and the check cards.
  const first = firstDayMode(i);
  const starting = first?.stage === "first_check";
  // Check in before the first reading (audit #8): the card's step 1 is the check-in, and it stands in for the gate card.
  const checkinFirst = starting && !!i.gate && !i.gate.ready && !i.hasCheckin;
  // F1-F4: food at risk. Failed TCS readings (a staff member doesn't get a manager's Remind/Call), then food to throw out.
  const order = { urgent: 0, high: 1, normal: 2 } as const;
  const riskFindings = i.findings.filter(isFoodAtRiskFinding).sort((a, b) => order[a.severity] - order[b.severity]);
  const riskCards: FeedCard[] = [
    ...riskFindings.map((f, n): FeedCard => {
      const g = manager ? f : { ...f, actions: f.actions.filter((a) => a.kind !== "remind" && a.kind !== "call" && a.kind !== "text") };
      return { kind: "finding", id: `risk-${n}-${f.ref ?? f.kind}`, finding: g, actions: g.actions };
    }),
    ...(i.foodToThrow ?? []).map((food): FeedCard => ({ kind: "throw_out", id: `throw-${food.id}`, food })),
  ];
  // F3: staff behind their own gate see the gate first. Everyone else: food at risk is the first card (F2).
  const staffGateFirst = !manager && !!i.gate && !i.gate.ready && !checkinFirst;
  if (!staffGateFirst) cards.push(...riskCards);
  if (first?.stage === "first_check") cards.push(checkinFirst
    ? { kind: "first_check", id: "first-check", title: FIRST_DAY_CHECKIN_TITLE, body: FIRST_DAY_CHECKIN_BODY, check: first.check, howTo: first.check.howTo, checkinFirst: true }
    : { kind: "first_check", id: "first-check", title: FIRST_CHECK_TITLE, body: "Your thermometer and a look inside each fridge and freezer. We'll go one step at a time.", check: first.check, howTo: first.check.howTo });
  if (first?.stage === "first_record") cards.push({ kind: "first_record", id: "first-record", title: FIRST_RECORD_TITLE, body: FIRST_RECORD_BODY });
  // 0. Owner in the evening: the one line comes first (moment map: "Evening → one line: controlled or not").
  const eveningLine = !first && i.role === "owner" && i.moment === "evening" && i.controlled;
  if (eveningLine)
    cards.push({ kind: "summary", id: "evening", title: i.controlled!.every ? "Today: every shift controlled" : "Today: one thing needs you", body: [i.controlled!.every ? "Every check-in done, every reading in range or fixed." : (i.controlled!.missing ?? "Open the Team tab for details."), i.teamDay ? `Today's work: ${i.teamDay}.` : ""].filter(Boolean).join(" ") });
  // 1. The only hard block comes first, for anyone who has it (T1). Managers and owners in the evening aren't starting
  //    a food shift, so a check-in prompt then is noise (the gate itself still guards Do).
  const showGate = !!i.gate && !i.gate.ready && !(manager && i.moment === "evening") && !checkinFirst;
  if (showGate && i.gate) {
    const needsCheckin = !i.hasCheckin;
    cards.push({ kind: "gate", id: "gate", title: needsCheckin ? "Check in before your shift" : "Not cleared for food work yet",
      body: needsCheckin ? "A few quick health questions, then wash your hands. About 30 seconds." : "Finish your lessons and sign-offs, about 20 minutes on your phone.",
      action: needsCheckin ? { label: "Start check-in", to: "checkin" } : { label: "Finish my lessons", to: "learn" } });
  }
  if (staffGateFirst) cards.push(...riskCards);
  // 1b. A manager asked me to look again at a log: private, one tap to fix it (teamwork.ts "Ask them to fix").
  for (const f of (i.fixAsks ?? []).slice(0, 3)) cards.push({ kind: "fix_ask", id: `fix-${f.id}`, headline: f.headline, entryId: f.entryId });
  // 2. Managers at opening: are we open for food?
  // At close and in the evening the question is the handoff and the day, not "can we open?" (moment map); a still-missing
  // opening check keeps its own card below.
  if (!starting && manager && i.openForFood && (i.moment === "before" || i.moment === "opening" || (i.moment === "day" && !i.openForFood.ready)))
    cards.push({ kind: "open_for_food", id: "open", ready: i.openForFood.ready, missing: i.openForFood.missing,
      ...(!i.openForFood.ready && i.openForFood.missing.length && i.openForFood.missing.every((m) => i.expected.find((e) => e.key === m.key)?.firstDay) ? { firstDay: true } : {}) });
  // 3. Reminders someone sent me.
  for (const [n, r] of (i.reminders ?? []).entries()) cards.push({ kind: "reminder", id: `rem-${n}`, from: "Your manager", body: r.message });
  // 3b. What was handed to me (the person in charge): newest three, right after reminders.
  for (const n of (i.handoffs ?? []).slice(0, 3)) cards.push({ kind: "handoff", id: `handoff-${n.id}`, notice: n });
  for (const t of (i.notices ?? []).slice(-1)) cards.push({ kind: "thanks", id: `thanks-${t.id}`, body: t.message });
  // 4. What's due now for me: late first, then due within the hour. The rush stays quiet otherwise.
  const soon = Date.parse(i.now) + 60 * 60_000;
  const due = i.expected.filter((e) => !e.satisfied && Date.parse(e.dueAt) <= soon).sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  // Checks that keep the kitchen from opening for food always get their card; the cap (3 for managers, 5 for staff)
  // only trims the rest, so a blocking opening check is never pushed out by earlier routine ones.
  const blockingKeys = new Set((i.openForFood?.ready ? [] : i.openForFood?.missing ?? []).map((m) => m.key));
  const cap = manager ? 3 : 5;
  const blockingDue = due.filter((e) => blockingKeys.has(e.key));
  const shown = [...blockingDue, ...due.filter((e) => !blockingKeys.has(e.key)).slice(0, Math.max(0, cap - blockingDue.length))].sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  // Staff behind the gate get the gate as their one thing: checks they can't open yet would be dead ends (T1).
  const staffBlocked = !manager && showGate;
  if (!starting && !staffBlocked && (!manager || i.moment !== "evening")) for (const e of shown) cards.push({ kind: "check", id: `check-${e.key}`, check: e, late: isLate(e, i.now), howTo: e.howTo });
  // 5. Managers: the findings with their prepared actions, then orders and gentle flags.
  if (manager) {
    // My own "check-in still needed" finding repeats the gate card above: show it once, as the gate.
    const mine = (f: Finding) => !!i.employeeId && f.ref === i.employeeId && (f.kind === "not_checked_in" || f.kind === "not_cleared");
    const fs = i.findings.filter((f) => f.kind !== "missed_check" && !isFoodAtRiskFinding(f) && !(i.gate && !i.gate.ready && mine(f))).sort((a, b) => order[a.severity] - order[b.severity]);
    for (const [n, f] of fs.entries()) cards.push({ kind: "finding", id: `f-${n}-${f.ref ?? f.kind}`, finding: f, actions: f.actions });
    if (i.orderNeeds && i.orderNeeds.count > 0) cards.push({ kind: "order", id: "order-list", needs: i.orderNeeds });
    for (const [n, fl] of (i.integrity ?? []).slice(0, 2).entries()) cards.push({ kind: "worth_a_look", id: `look-${n}`, flag: fl });
    // The owner's note about a quiet record (zeroreports.ts): never for a manager, never for staff.
    if (i.role === "owner" && i.zeroReports) cards.push({ kind: "zero_reports", id: "zero-reports", note: i.zeroReports });
  }
  // 5a. A shift lead in charge (staff): the open problems, without the manager-only people and scan findings or the
  //     Remind button (the API's remind is for managers; the nag button stays).
  if (!manager && i.inCharge) {
    const open = i.findings.filter((f) => (f.kind === "incident" || f.kind === "failed_check" || f.kind === "missed_check") && !isFoodAtRiskFinding(f)).sort((a, b) => order[a.severity] - order[b.severity]);
    for (const [n, f] of open.entries()) { const g = { ...f, actions: f.actions.filter((a) => a.kind !== "remind") }; cards.push({ kind: "finding", id: `f-${n}-${f.ref ?? f.kind}`, finding: g, actions: g.actions }); }
  }
  // 5b. Owners on Monday: last week in one line (docs/ANALYTICS.md, the retention item). Never ahead of what's due.
  if (i.role === "owner" && i.weekly?.showToday && !starting) cards.push({ kind: "summary", id: WEEKLY_SUMMARY_ID, title: "Your week", body: i.weekly.text });
  // 6. Wins: problems caught and fixed today (T3: truth is rewarded).
  if ((i.caughtEarlyToday ?? 0) > 0) cards.push({ kind: "caught_early", id: "caught", count: i.caughtEarlyToday! });
  // 6b. One private tip (learning, not an alert): after what's due, never ahead of it.
  const tip = (i.tips ?? []).filter((t) => !t.ackedAt).sort((a, b) => b.at.localeCompare(a.at))[0];
  if (tip) cards.push({ kind: "tip", id: `tip-${tip.id}`, tip });
  // 7. (The owner's evening line is card 0.)
  // 8. One refresher card a day, never during the rush for staff with work due.
  if (!starting && i.employeeId && !(i.moment === "day" && due.length)) {
    const r = refresherFor(i.employeeId, i.date);
    cards.push({ kind: "refresher", id: `ref-${r.lessonId}-${r.point}`, title: r.title, text: r.text, lessonId: r.lessonId });
  }
  // 9. The end: finite by design.
  // "All caught up" only when nothing above is still open: never under an open report or a check still due (audit #14b).
  const stillOpen = cards.some((c) => OPEN_KINDS.has(c.kind) || (c.kind === "open_for_food" && !c.ready));
  cards.push(stillOpen
    ? { kind: "caught_up", id: "end", title: END_OPEN_TITLE, body: END_OPEN_BODY }
    : { kind: "caught_up", id: "end", title: "You're all caught up", body: "Nothing needs you right now. Back to your guests.", ...(wantMoreOffer(i) ? { wantMore: true as const } : {}) });
  return cards;
}
